import ViewController from "./class.ViewController.js";
import TransporterViewLayout from "./class.TransporterViewLayout.js";
import TransporterSettings from "./class.TransporterSettings.js";
import TransporterErrors from "./class.TransporterErrors.js";
import TransporterCmty from "./class.TransporterCmty.js";
import TransporterTips from "./class.TransporterTips.js";
import * as TransporterUtils from "./class.TransporterUtils.js";

const iOS = TransporterUtils.detectIOS();

/**
 * The View controller for Transporter.
 * It handles stuff related to the view like scrolling.
 * It also maps contexts to their layout controllers.
 */
export default class TransporterView extends ViewController {
	/**
	 * A view controller which maintains the entire view of Transporter.
	 *
	 * @param {Object} options
	 * @param {*} options.model
	 * @param {*} options.collection
	 * @param {String} options.attributes.className
	 */
	constructor(options) {
		super(options);

		// Main window which we are wrapping.
		this.$main = $(`<div class="aops-transporter-main"></div>`).appendTo(
			this.$el
		);
		let mainController = options.attributes.mainController;
		AoPS.Transporter.Utils.getDocumentImageFile(
			"get_document_background_image",
			this.$main,
			mainController.document_id,
			mainController.version
		);

		// Track the sticky bubble.
		this.sticky = null;
		this.$stickyPlaceholder = null;

		this.isScrolling = false;
		this.lastScrollTop = this.$main.scrollTop();
		this.requestScrollTop = null;
		this.lastWidth = this.$el.width();

		// Prevent the view scrolljacking the user.
		this.stickyThreshold = 60;
		this.$main.on("wheel DOMMouseScroll mousewheel touchmove", (e) => {
			var scrollTop = this.$main.scrollTop();
			this.$main.stop(false, true);
			this.$main.scrollTop(scrollTop);
			if (this.sticky) {
				this._checkStickyInView();
			}
		});

		// Create the layout controller for the default context..
		this.layoutView = new TransporterViewLayout({
			attributes: {
				className: "aops-transporter-layout",
				mainView: this,
				progress: this.progress,
				mainController: this.mainController,
				context: this.context,
			},
		});

		// Make a container for layouts so we can adjust it's height manually.
		this.$layoutContainer = $('<div class="layout-container"></div>').appendTo(
			this.$main
		);
		this.layoutView.$el.appendTo(this.$layoutContainer);

		// Tracking layout views with their context.
		this.contextLayouts = {
			default: this.layoutView,
		};

		// Make a container for scroll alert.
		this.scrollAlert = false;
		this.$scrollAlert = $(`<div class="scroll-alert"></div>`)
			.appendTo(this.$el)
			.hide();

		// Make our settings thingy.
		this.settingsView = new TransporterSettings({
			attributes: {
				className: "aops-transporter-settings",
				mainView: this,
				mainController: this.mainController,
			},
		});
		this.settingsView.$el.appendTo(this.$el).addClass("overlay");
		this.$el.click((e) => {
			if (
				this.settingsView.$el.is(":visible") &&
				!this.settingsView.$el.is(e.target) &&
				!this.settingsView.$el.has(e.target).length
			) {
				this.settingsView.closeSettingsMenu();
			}
		});

		// Make our errors thingy.
		this.errorsView = new TransporterErrors({
			attributes: {
				className: "aops-transporter-errors",
				mainView: this,
				mainController: this.mainController,
			},
		});
		this.errorsView.$el.appendTo(this.$el).addClass("overlay");

		/**
		 * Build the tips controller, which fetches formatting tips.
		 */
		this.tipsView = new TransporterTips({
			attributes: {
				className: "aops-transporter-tips",
				mainView: this,
				mainController: this.mainController,
			},
		});
		this.tipsView.$el.appendTo(this.$el).addClass("overlay");

		// Make our community thingy.
		if (this.enableCmty) {
			this.cmtyView = new TransporterCmty({
				attributes: {
					className: "aops-transporter-cmty",
					mainView: this,
					mainController: this.mainController,
				},
			});
			this.cmtyView.$el.appendTo(this.$el).addClass("overlay");
		}

		setInterval(() => {
			this.watchHeightReflow();
		}, 200);
	}

	reset() {
		this.isScrolling = false;
		this.scrollAlert = false;
		this.contextLayouts = {
			default: this.layoutView,
		};
		this.sticky = null;
		if (this.$stickyPlaceholder) {
			this.$stickyPlaceholder.remove();
		}
		this.$stickyPlaceholder = null;
		this.layoutView.$el.html("").siblings().remove();
		this.$layoutContainer.css("height", "");
		this.sticky = null;
		this.$stickyPlaceholder = null;
	}

	watchHeightReflow() {
		if (this.$el.is(":visible")) {
			const scrollTop = this.$main.scrollTop();
			const currentHeight = this.$layoutContainer.height();
			const realWidth = this.$el.width();
			const realHeight = this.$layoutContainer.height("").height();
			this.$layoutContainer.height(Math.max(currentHeight, realHeight));
			this.$layoutContainer.width(realWidth + "px");
			this.$main.scrollTop(scrollTop);

			if (this.sticky) {
				this._checkStickyInView();
			}

			if (this.requestScrollTop) {
				this.$main.scrollTop(this.requestScrollTop);
				this.requestScrollTop = null;
			}

			this.lastScrollTop = this.$main.scrollTop();

			// Deal with scroll alert
			const scrollMax = this._calculateScrollMax();
			const alertMargin = 200;
			const stopAlertMargin = 100;

			if (this.scrollAlert) {
				if (scrollMax - scrollTop <= stopAlertMargin) {
					this.scrollAlert = false;
					this.$scrollAlert.fadeOut(500);
				}
			} else if (scrollMax - scrollTop > alertMargin) {
				this.scrollAlert = true;
				this.$scrollAlert.fadeIn(500);
			}

			// Shrink wrap on width changes.
			if (this.lastWidth !== realWidth) {
				this.$layoutContainer.height("");
				if (!this.scrollAlert) {
					this.snapToBottom();
				}
			}
			this.lastWidth = realWidth;
		}
	}

	/**
	 * Returns the TransporterViewLayout for a context.
	 *
	 * @param {number} context
	 * @param {*} $anchor
	 * @returns {TransporterViewLayout}
	 */
	getContextLayout(context, $anchor) {
		if (!_.isNumber(context)) {
			return this.contextLayouts.default;
		}
		if (_.isUndefined(this.contextLayouts[context])) {
			// Create a layout controller for this new context.
			this.contextLayouts[context] = new TransporterViewLayout({
				attributes: {
					className: "aops-transporter-layout",
					mainView: this,
					progress: this.progress,
					mainController: this.mainController,
					context: context,
				},
			});
			if ($anchor) {
				this.contextLayouts[context].$el.insertAfter($anchor);
			} else {
				this.contextLayouts[context].$el.appendTo(this.$layoutContainer);
			}
		}
		return this.contextLayouts[context];
	}

	/**
	 * Scrolls the view to the target element.
	 *
	 * @param {*} $target
	 * @param {*} duration
	 */
	scrollToTarget($target, duration) {
		if (_.isUndefined(duration)) {
			duration = 1000;
		}

		return new Promise((resolve) => {
			const threshold = duration ? this.stickyThreshold : Infinity;
			let scrollTo = this._calculateScrollTo($target, threshold);

			if (duration === 0) {
				this.$main.scrollTop(scrollTo);
				resolve();
			} else if (this.$main.scrollTop() === scrollTo) {
				//If the scrolling is already at its destination, then no animation needed.
				resolve();
			} else {
				this.isScrolling = true;

				this.$main.animate(
					{scrollTop: scrollTo},
					{
						duration:
							duration *
							this.mainController.animationController.getDurationModifier(),
						complete: () => {
							this.isScrolling = false;
							resolve();
						},
						step: (now, fx) => {
							const newScrollTo = this._calculateScrollTo($target, threshold);

							if (scrollTo < newScrollTo) {
								scrollTo = newScrollTo;
								fx.end = scrollTo;
							}
						},
					}
				);
			}
		});
	}

	/**
	 * Snaps the scroller to the bottom.
	 */
	snapToBottom() {
		const $view = this.$main;

		if (iOS) {
			$view.scrollTop(this._calculateScrollMax());
		} else {
			$view.scrollTop(Number.MAX_SAFE_INTEGER);
		}
		if (this.$main.is(":visible")) {
			this.lastScrollTop = this.$main.scrollTop();
		}
	}

	/**
	 * Used by non-Transporter classes to request Transporter scrolls to
	 * a certain scroll top.
	 * @param {number} scrollTop
	 */
	requestScroll(scrollTop) {
		this.requestScrollTop = scrollTop;
	}

	/**
	 * Sets opacity to 0 for milliseconds.
	 * @param {number} milliseconds
	 */
	hideFor(milliseconds) {
		this.$main.css({
			opacity: "0",
		});
		setTimeout(() => {
			this.unhide();
		}, milliseconds);
	}

	/**
	 * Stop hiding the main view.
	 */
	unhide() {
		this.$main.css({
			opacity: "",
		});
	}

	/**
	 * Whether the main view is hidden.
	 */
	isHidden() {
		return parseInt(this.$main.css("opacity")) === 0;
	}

	/**
	 * Renders a play button over the view. Resolves when the user clicks play.
	 */
	showPlayButtonAndWait() {
		return new Promise((resolve) => {
			const $play = $(
				`<div class="transporter-play-button overlay"><div class="play-button">v</div></div>`
			);
			const $icons = this.$el.find(".overlay .menu-icon:visible");
			$icons.hide();

			if (TransporterUtils.detectIncompatibleBrowser()) {
				$play.appendTo(this.$el).find(".play-button").addClass("ie-error")
					.html(`
						<p>Your browser is not compatible with Self-Paced Lessons.</p>
						<p>Please download a recent version of Chrome, Safari, Firefox, or Edge.</p>
					`);
			} else {
				$play
					.click(() => {
						$play.remove();
						$icons.show();
						resolve();
					})
					.appendTo(this.$el);
			}
		});
	}

	/**
	 * Assigns a sticky.
	 * @param {*} bubbleView
	 */
	_sticky(bubbleView) {
		this._desticky();
		this.sticky = bubbleView;
		this._checkStickyInView();
	}

	/**
	 * Unassigns the current sticky.
	 */
	_unsticky() {
		if (this.sticky) {
			this._desticky();
			this.sticky.$el.removeClass("collapsed");
			this.sticky = null;
			this.$stickyPlaceholder = null;
		}
	}

	/**
	 * Removes the sticky class from the current sticky
	 * and destroys the sticky placeholder.
	 */
	_desticky() {
		if (this.sticky) {
			this.sticky.$el
				.removeClass("sticky")
				.children(".sticky-collapse")
				.remove();
			if (this.$stickyPlaceholder) {
				this.$stickyPlaceholder.remove();
				this.$stickyPlaceholder = null;
			}
		}
	}

	/**
	 * Collapses the sticky and then unstickies it.
	 * @return {Promise} A promise that resolves when the collapse animation ends.
	 */
	collapseThenUnsticky() {
		return new Promise((resolve) => {
			if (!this.mainController.animationController.isEnabled()) {
				this._unsticky();
				resolve();
			} else if (this.sticky) {
				const sticky = this.sticky;
				if (sticky.$el.hasClass("collapsed")) {
					this._unsticky();
					resolve();
				} else if (sticky.$el.hasClass("sticky")) {
					sticky.$el.addClass("collapsed").one("transitionend", () => {
						if (this.sticky === sticky) {
							this._unsticky();
						}
						resolve();
					});
				} else {
					this._unsticky();
					resolve();
				}
			} else {
				resolve();
			}
		});
	}

	/**
	 * Checks if the sticky is beyond the top of the view.
	 * If so, it gives it the sticky class and creates the
	 * sticky placeholder.
	 */
	_checkStickyInView() {
		if (!this.sticky || this.sticky.$el.hasClass("transitioning")) {
			return;
		}
		const $target = this.$stickyPlaceholder || this.sticky.$el;
		const $view = this.$main;
		const viewTop = $view.offset().top - parseInt($view.css("margin-top"));
		const targetTop =
			$target.offset().top - parseInt($target.css("margin-top"));

		if (targetTop <= viewTop) {
			if (!this.sticky.$el.hasClass("sticky")) {
				// Sticky is not in view... stick it!
				const height = this.sticky.$el.outerHeight();
				this.sticky.$el.addClass("sticky");
				this.sticky.$el.append(this._newStickyCollapse());
				this.$stickyPlaceholder = $(`<div class="sticky-placeholder"/>`);
				this.$stickyPlaceholder.css({height}).insertBefore(this.sticky.$el);
			}
		} else {
			if (this.sticky.$el.hasClass("sticky")) {
				// Sticky is in view... desticky it!
				this._desticky();
				if (this.sticky.$el.hasClass("collapsed")) {
					this.sticky.$el
						.removeClass("collapsed")
						.addClass("transitioning")
						.one("transitionend", () => {
							this.sticky.$el.removeClass("transitioning");
						});
				}
			}
		}
	}

	_newStickyCollapse() {
		return $(
			`<div class="sticky-collapse"><span class="aops-font">7</span></div>`
		).click(() => {
			if (this.sticky) {
				this.sticky.$el.toggleClass("collapsed");
			}
		});
	}

	/**
	 * Calculates the new scrollTop value to get to target.
	 *
	 * @param {*} $target The target to scroll to
	 * @param {*} threshold The max distance before no scroll is performed.
	 */
	_calculateScrollTo($target, threshold) {
		const $view = this.$main;
		const targetTop =
			$target.offset().top - parseInt($target.css("margin-top"));
		const targetBottom = targetTop + $target.outerHeight(true);
		const viewTop = $view.offset().top - parseInt($view.css("margin-top"));
		const viewBottom = viewTop + $view.outerHeight(true);
		const offsetTop = targetTop - viewTop;
		const offsetBottom = targetBottom - viewBottom;
		const minDistance = targetTop - viewBottom;
		const scrollMargin = 0;
		// const scrollMax = Math.max(
		// 	0,
		// 	$view.prop("scrollHeight") - $view.outerHeight(true)
		// );

		if (
			targetTop < viewTop || // Top is above view
			(viewBottom < targetBottom && minDistance < threshold) // Bottom is below view
		) {
			let scrollTo;
			let desiredScroll;
			if (targetTop < viewTop) {
				desiredScroll = $view.scrollTop() + offsetTop - scrollMargin;
				// scrollTo = Math.max(scrollMin, desiredScroll);
				scrollTo = desiredScroll;
			} else {
				desiredScroll = $view.scrollTop() + offsetBottom + scrollMargin;
				// scrollTo = Math.min(scrollMax, desiredScroll);
				scrollTo = desiredScroll;
			}
			scrollTo = Math.floor(scrollTo);

			return scrollTo;
		} else {
			return $view.scrollTop();
		}
	}

	/**
	 * Returns the maximum scrollTop.
	 */
	_calculateScrollMax() {
		const scrollMax = Math.max(
			0,
			this.$main.prop("scrollHeight") - this.$main.outerHeight(true)
		);
		return scrollMax;
	}
}
